import { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient.js';
import { normalizeFuel } from '../utils/normalization.js';

export function useListings() {
  const [allListings, setAllListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableMakes, setAvailableMakes] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [availableVersions, setAvailableVersions] = useState([]);
  const [availableFuelTypes, setAvailableFuelTypes] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    async function fetchListings() {
      try {
        // Paginate to fetch ALL listings (Supabase defaults to 1000 rows)
        const PAGE_SIZE = 1000;
        let allData = [];
        let page = 0;
        let hasMore = true;

        while (hasMore) {
          const { data, error } = await supabase
            .from('car_listings')
            .select('*')
            .eq('status', 'active')
            .order('price', { ascending: false })
            .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

          if (error) throw error;
          allData = allData.concat(data);
          hasMore = data.length === PAGE_SIZE;
          page++;
        }

        if (allData.length > 0) {
          setAllListings(allData);
          const data = allData;

          // Extract unique countries
          const countries = [...new Set(data.map(car => car.source_country))].filter(Boolean);
          setAvailableCountries(countries);

          // Note: default target is set by getDefaultTarget() in useArbitrageFilters
          // based on the user's timezone/locale — no override needed here

          // Makes are already normalized at ingestion — trust DB values
          const makes = [...new Set(data.map(car => car.make))].filter(Boolean).sort();
          setAvailableMakes(makes);

          // Extract unique models (will be filtered by make in the dropdown)
          const models = [...new Set(data.map(car => car.model))].filter(Boolean).sort();
          setAvailableModels(models);

          // Versions are pre-normalized at ingestion in normalized_version column
          const versions = [...new Set(data.map(car => car.normalized_version))].filter(Boolean).sort();
          setAvailableVersions(versions);

          // Extract unique fuel types and normalize
          const fuelTypes = [...new Set(data.map(car => normalizeFuel(car.fuel_type)))].filter(Boolean).sort();
          setAvailableFuelTypes(fuelTypes);
        }
      } catch (error) {
        console.error('Error fetching listings:', error.message);
      } finally {
        setLoading(false);
      }
    }

    fetchListings();

    // Fetch last update timestamp via API (uses service key, bypasses RLS)
    fetch('/api/last-update')
      .then(r => r.json())
      .then(d => { if (d.lastUpdate) setLastUpdate(d.lastUpdate); })
      .catch(() => {});

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    allListings,
    loading,
    availableCountries,
    availableMakes,
    availableModels,
    availableVersions,
    availableFuelTypes,
    lastUpdate,
  };
}
